/**
 * Store réactif du compteur de mutations en attente — Phase 3.3.4.
 *
 * Expose un `pendingCount` qui reflète le nombre de mutations
 * actuellement queued dans IDB. La source de vérité reste l'IDB
 * (`mutationsQueue.ts`) — ce store n'est qu'un mirror réactif pour
 * permettre à l'UI de réagir (badge, toast, etc.) sans poller IDB.
 *
 * Stratégie de mise à jour :
 *
 *   1. **Hydratation au boot** : `hydratePendingCount()` est appelée
 *      par `main.ts` (ou par `installMutationsReplay`) au démarrage
 *      pour rafraîchir le compteur depuis IDB.
 *   2. **Increment optimiste** : à chaque `sendOrQueueMutation` qui
 *      retourne `kind: 'queued'`, on bump `+1` localement.
 *   3. **Refresh après replay** : à la fin de `replayPendingMutations`,
 *      on re-query IDB pour avoir un compte exact (le drain a
 *      retiré N mutations, certaines peuvent rester).
 *
 * Pas d'erreur silencieuse : si l'hydratation échoue (IDB KO), on
 * laisse `pendingCount = 0` — l'UI ne montrera pas de badge mais
 * l'app reste utilisable.
 */
import { createStore } from '../lib/store.js';
import { countPendingMutations } from '../storage/mutationsQueue.js';

export interface MutationsQueueState {
  pendingCount: number;
  /** Timestamp ms du dernier refresh — utile pour le debug + UI
   *  conditionnelle (« Synchronisé il y a 2 s »). */
  lastRefreshAt: number | null;
}

const initialState: MutationsQueueState = {
  pendingCount: 0,
  lastRefreshAt: null,
};

const store = createStore<MutationsQueueState>(initialState);

/**
 * Lecture immédiate de l'état courant — utile aux templates lit-html
 * qui veulent rendre le compteur sans s'abonner.
 */
export function getMutationsQueueState(): MutationsQueueState {
  return store.getState();
}

/**
 * Abonne un listener aux changements. Retourne l'unsubscribe.
 * Pattern aligné sur les autres stores Nestor.
 */
export function subscribeMutationsQueue(
  listener: (state: MutationsQueueState) => void,
): () => void {
  return store.subscribe(listener);
}

/**
 * Rafraîchit le compteur depuis IDB. À appeler :
 *   - Au boot (avant le replay initial).
 *   - Après chaque replay (drain terminé).
 *   - Manuellement si l'UI veut forcer un refresh (ex. après login).
 */
export async function hydratePendingCount(): Promise<void> {
  const count = await countPendingMutations();
  store.setState({ pendingCount: count, lastRefreshAt: Date.now() });
}

/**
 * Increment optimiste — appelé par `sendOrQueueMutation` quand
 * `kind: 'queued'`. Évite un round-trip IDB pour un simple ++.
 */
export function bumpPendingCount(): void {
  const current = store.getState().pendingCount;
  store.setState({ pendingCount: current + 1, lastRefreshAt: Date.now() });
}

/** @internal pour tests — reset l'état. */
export function _resetMutationsQueueStoreForTests(): void {
  store.setState(initialState, true);
}
